import React, { useState, useEffect } from "react";
import { FaBarcode, FaDownload, FaPrint, FaCopy, FaPlus, FaHistory } from "react-icons/fa";
import html2canvas from "html2canvas";
import { barcodeAPI } from "../api/barcode";
import "./Barcode.css";

export default function Barcode() {
  const [category, setCategory] = useState("");
  const [sku, setSku] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [labels, setLabels] = useState([]);
  const [currentLabel, setCurrentLabel] = useState("");
  const [generatedBarcode, setGeneratedBarcode] = useState(null);
  const [barcodeHistory, setBarcodeHistory] = useState([]);
  const [categories, setCategories] = useState(["Electronics", "Apparel", "Warehouse Tools"]);
  const [previewUrl, setPreviewUrl] = useState("");

  // Fetch real data from API
  useEffect(() => {
    const fetchBarcodeHistory = async () => {
      try {
        const response = await barcodeAPI.getAll();
        if (response.data.success) {
          setBarcodeHistory(response.data.data);
        }
      } catch (error) {
        console.error("Error fetching barcode history:", error);
      }
    };

    fetchBarcodeHistory();
  }, []);

  // Update preview when SKU changes - Generate real barcode
  useEffect(() => {
    if (sku.trim()) {
      // Generate real barcode data (not image URL)
      const barcodeData = {
        sku: sku.trim(),
        text: sku.trim(),
        type: 'code128'
      };
      setPreviewUrl(barcodeData);
    } else {
      setPreviewUrl(null);
    }
  }, [sku]);

  const addLabel = () => {
    if (currentLabel.trim() && !labels.includes(currentLabel.trim())) {
      setLabels([...labels, currentLabel.trim()]);
      setCurrentLabel("");
    }
  };

  const removeLabel = (labelToRemove) => {
    setLabels(labels.filter(label => label !== labelToRemove));
  };

  // Helper function to scale barcode based on SKU length
  const getBarcodeScale = (length) => {
    if (length <= 10) return 1;
    if (length <= 20) return 0.85;
    if (length <= 30) return 0.7;
    if (length <= 40) return 0.6;
    return 0.5;
  };

  // Generate Code128 bars (real barcode without images)
  const generateCode128Bars = (text) => {
    // Simple Code128 pattern generation
    const patterns = {
      '0': '11011001100', '1': '11001101100', '2': '11001100110', '3': '10010011000',
      '4': '10010001100', '5': '10001001100', '6': '10001001110', '7': '10100010010',
      '8': '10001000110', '9': '10000100110', 'A': '10100011000', 'B': '10001011000',
      'C': '11001000010', 'D': '10001000100', 'E': '10000011000', 'F': '11000010010',
      'G': '10100001000', 'H': '10000101000', 'I': '10000010110', 'J': '11000010100',
      'K': '10001110100', 'L': '11000110100', 'M': '10100001100', 'N': '10000101100',
      'O': '10000001110', 'P': '11000001110', 'Q': '10100001010', 'R': '10000101010',
      'S': '11000100010', 'T': '11000010000', 'U': '11000000100', 'V': '11000000110',
      'W': '10100001110', 'X': '10000101110', 'Y': '10000010100', 'Z': '10000010110',
      '-': '10000100010'
    };

    return text.split('').map((char, index) => {
      const pattern = patterns[char.toUpperCase()] || patterns['0'];
      return (
        <div key={index} className="barcode-bar">
          {pattern.split('').map((bit, bitIndex) => (
            <div 
              key={bitIndex} 
              className={`barcode-segment ${bit === '1' ? 'black' : 'white'}`}
            />
          ))}
        </div>
      );
    });
  };

  const generateBarcode = async () => {
    if (!sku || !category) {
      alert("Please fill in SKU and select a category");
      return;
    }

    // Show loading state
    setGeneratedBarcode(null);
    
    try {
      console.log("🔄 Generating barcode with data:", { sku, category, quantity, labels });
      
      const response = await barcodeAPI.create({
        sku,
        category,
        quantity,
        labels
      });

      console.log("📡 Barcode API response:", response);

      if (response.data && response.data.success) {
        const newBarcode = response.data.data;
        setGeneratedBarcode(newBarcode);
        setBarcodeHistory([newBarcode, ...barcodeHistory]);
        
        // Reset form
        setSku("");
        setCategory("");
        setQuantity(1);
        setLabels([]);
        setCurrentLabel("");
        
        alert("✅ Barcode generated successfully!");
      } else {
        const errorMessage = response.data?.message || "Failed to generate barcode";
        console.error("❌ Barcode generation failed:", response.data);
        
        // Enhanced error messages based on status
        if (response.status === 500) {
          alert("🔧 Server Error: Barcode generation service is temporarily unavailable. Please try again in a few minutes.");
        } else if (response.status === 400) {
          alert("⚠️ Invalid Data: Please check your input and try again.");
        } else if (response.status === 401) {
          alert("🔒 Unauthorized: Please check your permissions and try again.");
        } else if (response.status === 403) {
          alert("🚫 Forbidden: You don't have permission to generate barcodes.");
        } else if (response.status === 404) {
          alert("🔍 Not Found: Barcode service not found. Please contact administrator.");
        } else {
          alert(`❌ Error (${response.status || 'Unknown'}): ${errorMessage}`);
        }
      }
    } catch (error) {
      console.error("❌ Network error generating barcode:", error);
      
      // Enhanced network error handling
      if (error.code === 'ECONNREFUSED') {
        alert("🌐 Connection Refused: Server is not running. Please check if the backend server is started.");
      } else if (error.code === 'ENOTFOUND') {
        alert("🔍 Server Not Found: Backend server not available. Please check the server URL.");
      } else if (error.code === 'ETIMEDOUT') {
        alert("⏰ Connection Timeout: Server is taking too long to respond. Please try again.");
      } else if (error.code === 'ERR_NETWORK') {
        alert("📶 Network Error: Please check your internet connection.");
      } else {
        alert(`❌ Unexpected Error: ${error.message || 'Unknown error occurred'}`);
      }
    }
  };

const downloadBarcode = (barcode) => {
    // Create canvas for barcode
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    // Set canvas size
    canvas.width = 400;
    canvas.height = 120;
    
    // White background
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Generate Code128 bars
    const patterns = {
      '0': '11011001100', '1': '11001101100', '2': '11001100110', '3': '10010011000',
      '4': '10010001100', '5': '10001001100', '6': '10001001110', '7': '10100010010',
      '8': '10001000110', '9': '10000100110', 'A': '10100011000', 'B': '10001011000',
      'C': '11001000010', 'D': '10001000100', 'E': '10000011000', 'F': '11000010010',
      'G': '10100001000', 'H': '10000101000', 'I': '10000010110', 'J': '11000010100',
      'K': '10001110100', 'L': '11000110100', 'M': '10100001100', 'N': '10000101100',
      'O': '10000001110', 'P': '11000001110', 'Q': '10100001010', 'R': '10000101010',
      'S': '11000100010', 'T': '11000010000', 'U': '11000000100', 'V': '11000000110',
      'W': '10100001110', 'X': '10000101110', 'Y': '10000010100', 'Z': '10000010110',
      '-': '10000100010'
    };
    
    let xPosition = 20;
    const barWidth = 2;
    const barHeight = 60;
    
    // Draw bars for each character
    for (const char of barcode.sku.toUpperCase()) {
      const pattern = patterns[char] || patterns['0'];
      for (const bit of pattern) {
        ctx.fillStyle = bit === '1' ? 'black' : 'white';
        ctx.fillRect(xPosition, 20, barWidth, barHeight);
        xPosition += barWidth;
      }
      xPosition += 2; // Gap between characters
    }
    
    // Add text below barcode
    ctx.fillStyle = 'black';
    ctx.font = '14px monospace';
    ctx.textAlign = 'center';
    ctx.fillText(barcode.sku, canvas.width / 2, 95);
    
    // Download the canvas as image
    canvas.toBlob((blob) => {
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.download = `barcode-${barcode.sku}.png`;
      link.href = url;
      link.click();
      URL.revokeObjectURL(url);
    });
  };


  const copyCode = (sku) => {
    navigator.clipboard.writeText(sku);
    alert("SKU copied to clipboard!");
  };

  const printBarcode = (barcode) => {
    window.print();
  };

  return (
    <div className="barcode-page">
      <div className="barcode-header">
        <h1><FaBarcode /> Barcode Generator</h1>
        <p>Generate and manage barcodes for your inventory items</p>
      </div>

      <div className="barcode-cards-container">
        {/* LEFT CARD - GENERATE BARCODE */}
        <div className="barcode-card generate-card">
          <h2>Generate Barcode</h2>
          
          <div className="form-group">
            <label>Select Category</label>
            <select 
              value={category} 
              onChange={(e) => setCategory(e.target.value)}
              className="form-input"
            >
              <option value="">Choose category...</option>
              {categories.map((cat, idx) => (
                <option key={idx} value={cat}>{cat}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Barcode Number / SKU</label>
            <input
              type="text"
              placeholder="e.g. SKU-88219-B"
              value={sku}
              onChange={(e) => setSku(e.target.value)}
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label>Labels</label>
            <div className="labels-container">
              <div className="labels-list">
                {labels.map((label, idx) => (
                  <span key={idx} className="label-pill">
                    {label}
                    <button 
                      onClick={() => removeLabel(label)}
                      className="label-remove"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
              <div className="add-label-container">
                <input
                  type="text"
                  placeholder="Add label..."
                  value={currentLabel}
                  onChange={(e) => setCurrentLabel(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && addLabel()}
                  className="add-label-input"
                />
                <button onClick={addLabel} className="add-label-btn">
                  <FaPlus /> Add Label
                </button>
              </div>
            </div>
          </div>

          <div className="form-group">
            <label>Quantity</label>
            <input
              type="number"
              min="1"
              value={quantity}
              onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
              className="form-input"
            />
          </div>

          <button 
            onClick={generateBarcode}
            className="generate-btn primary-btn"
          >
            <FaBarcode /> Generate Barcode
          </button>
        </div>

        {/* RIGHT CARD - BARCODE PREVIEW */}
        <div className="barcode-card preview-card">
          <h2>Barcode Preview</h2>
          
          {previewUrl ? (
            <div className="preview-content">
              <div className="barcode-display">
                {/* Real Code128 Barcode Display */}
                <div className="code128-barcode">
                  <div 
                    className="barcode-lines"
                    style={{ transform: `scaleX(${getBarcodeScale(previewUrl.text.length)})` }}
                  >
                    {generateCode128Bars(previewUrl.text)}
                  </div>
                  <div className="barcode-text">{previewUrl.text}</div>
                </div>
              </div>
              
              {sku && (
                <div className="barcode-details">
                  <p><strong>SKU:</strong> {sku}</p>
                  {category && <p><strong>Category:</strong> {category}</p>}
                  <p><strong>Quantity:</strong> {quantity}</p>
                  {labels.length > 0 && (
                    <div className="barcode-labels">
                      <strong>Labels:</strong>
                      <div className="label-pills">
                        {labels.map((label, index) => (
                          <span key={index} className="label-pill">{label}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="barcode-placeholder">
              <FaBarcode size={48} color="#ccc" />
              <p>Enter SKU to generate barcode preview</p>
            </div>
          )}
          
          {sku && (
            <div className="barcode-actions">
              <button className="action-btn download-btn" onClick={() => downloadBarcode({ sku, barcode_url: `/api/barcodes/image/${sku}` })}
>
                <FaDownload /> Download
              </button>
              <button className="action-btn print-btn" onClick={() => printBarcode({ sku, barcode_url: previewUrl })}>
                <FaPrint /> Print
              </button>
              <button className="action-btn copy-btn" onClick={() => copyCode(sku)}>
                <FaCopy /> Copy Code
              </button>
            </div>
          )}
        </div>
      </div>

      {/* BARCODE HISTORY SECTION */}
      <div className="barcode-history-section">
        <div className="history-header">
          <h2><FaHistory /> Barcode History</h2>
          <a href="#" className="view-all-link">View all history</a>
        </div>

        <div className="history-table-container">
          <table className="history-table">
            <thead>
              <tr>
                <th>Barcode</th>
                <th>Date</th>
                <th>Category</th>
                <th>Quantity</th>
                <th>Labels</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {barcodeHistory.map((item) => {
                return (
                <tr key={item.id}>
                  <td>
                    <div className="code128-barcode small">
                      <div 
                        className="barcode-lines"
                        style={{ transform: `scaleX(${getBarcodeScale(item.sku.length)})` }}
                      >
                        {generateCode128Bars(item.sku)}
                      </div>
                      <div className="barcode-text small">{item.sku}</div>
                    </div>
                  </td>
                  <td>{item.date}</td>
                  <td>{item.category}</td>
                  <td>{item.quantity}</td>
                  <td>
                    <div className="labels-list">
                      {item.labels.map((label, idx) => (
                        <span key={idx} className="label-pill small">{label}</span>
                      ))}
                    </div>
                  </td>
                  <td>
                    <button 
                      onClick={() => downloadBarcode(item)}
                      className="download-btn small-btn"
                    >
                      <FaDownload />
                    </button>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
